import React, { useEffect } from "react";
import { Box, Heading, HStack, Link, Tag, Text, VStack } from "@chakra-ui/react";
import { PageHeader } from "@lubbezposrednio-frontend/commons";
import { Link as RouterLink } from "react-router-dom";
import { publicApiBaseUrl } from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box as="pre" bg="#1d1530" color="#e9e3f7" borderRadius="10px" p="14px 16px" fontSize="13px" fontFamily="mono" overflowX="auto" whiteSpace="pre">
    {children}
  </Box>
);

const Endpoint: React.FC<{ method: string; path: string; auth: boolean; desc: string }> = ({ method, path, auth, desc }) => (
  <HStack align="start" spacing="10px" py="9px" borderBottom="1px solid #f0ecf7">
    <Tag size="sm" colorScheme="purple" minW="46px" justifyContent="center" fontWeight="800">{method}</Tag>
    <Box flex="1" minW="0">
      <Text fontFamily="mono" fontSize="13px" color="gray.800" wordBreak="break-all">{path}</Text>
      <Text fontSize="12px" color="gray.500">{desc}</Text>
    </Box>
    <Tag size="sm" colorScheme={auth ? "orange" : "green"}>{auth ? "klucz" : "publiczne"}</Tag>
  </HStack>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box bg="white" borderRadius="16px" border="1px solid #efeaf8" p="20px">
    <Heading fontSize="16px" color="gray.800" mb="12px">{title}</Heading>
    {children}
  </Box>
);

export const ApiDocsView: React.FC = () => {
  useEffect(() => {
    document.title = "Dokumentacja API";
  }, []);

  const base = publicApiBaseUrl();

  return (
    <OrganizerPage header={<PageHeader title="Dokumentacja API" subtitle="Publiczne API do integracji wyników z Twoimi systemami." />}>
      <VStack align="stretch" spacing="16px">
        <Section title="Wprowadzenie">
          <Text fontSize="14px" color="gray.600" mb="10px">
            Publiczne API jest <b>tylko do odczytu</b> — pozwala pokazać i zweryfikować wyniki Twoich głosowań na własnej
            stronie (np. portalu urzędu). Zwraca dane zbiorcze i dowód zakotwiczenia na blockchainie; nigdy nie ujawnia danych
            osobowych wyborców.
          </Text>
          <Text fontSize="13px" color="gray.500" mb="4px">Bazowy adres:</Text>
          <CodeBlock>{base}</CodeBlock>
        </Section>

        <Section title="Uwierzytelnianie">
          <Text fontSize="14px" color="gray.600" mb="10px">
            Każde żądanie (poza <Text as="span" fontFamily="mono">health</Text> i widgetem) wymaga Twojego klucza API w nagłówku.{" "}
            Klucz wygenerujesz w zakładce{" "}
            <Text as={RouterLink} to="/organizator/api" color="primary" fontWeight="700" textDecoration="underline">Klucz API</Text>.
          </Text>
          <CodeBlock>{`curl -H "Authorization: Bearer lbp_live_…" \\\n  ${base}/me`}</CodeBlock>
          <Text fontSize="12px" color="gray.500" mt="8px">Limit: 600 żądań/godz. na klucz. Po przekroczeniu — odpowiedź 429.</Text>
        </Section>

        <Section title="Endpointy">
          <Endpoint method="GET" path="/health" auth={false} desc="Status usługi." />
          <Endpoint method="GET" path="/me" auth desc="Informacje o właścicielu klucza." />
          <Endpoint method="GET" path="/votings" auth desc="Lista Twoich głosowań (status, daty, liczba uprawnionych)." />
          <Endpoint method="GET" path="/votings/:id" auth desc="Szczegóły głosowania + pytania i odpowiedzi." />
          <Endpoint method="GET" path="/votings/:id/results" auth desc="Frekwencja i werdykty (po zakończeniu i publikacji)." />
          <Endpoint method="GET" path="/votings/:id/verification" auth desc="Dowód zakotwiczenia on-chain: txid, odcisk, werdykt zgodności." />
          <Endpoint method="GET" path="/votings/:id/widget" auth={false} desc="Osadzalny iframe z dowodem (tylko głosowania publiczne)." />
        </Section>

        <Section title="Widget weryfikacji">
          <Text fontSize="14px" color="gray.600" mb="10px">
            Najprostszy sposób na pokazanie mieszkańcom, że wynik jest zapieczętowany na blockchainie — wklej iframe na swoją stronę
            (działa bez klucza dla głosowań publicznych):
          </Text>
          <CodeBlock>{`<iframe\n  src="${base}/votings/UUID/widget"\n  style="border:0;width:380px;height:260px"\n  loading="lazy"></iframe>`}</CodeBlock>
        </Section>

        <Text fontSize="13px" color="gray.500">
          Potrzebujesz zapisu (tworzenie głosowań, wgrywanie uprawnionych) albo szerszej integracji?{" "}
          <Link href="mailto:kontakt@potrafiszpolsko.pl" color="primary" fontWeight="700">Napisz do nas</Link>.
        </Text>
      </VStack>
    </OrganizerPage>
  );
};
